import {isPisAllowedForPartyType} from '@sollapay/domain';
import {LinkExpiryDuration, PaymentMethod} from '@sollapay/enums';
import {
  Badge,
  ButtonGroup,
  FieldMoneyInput,
  FieldSelect,
  Icon,
  IconBox,
  InfoTooltip,
  Typography
} from '@sollapay/ui/components';
import {formatNIS, Money, parseMoney} from '@sollapay/utils';
import {useCallback, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {config} from '@/app/app.config';
import {FormShell} from '@/components';
import {DEFAULT_PAYMENT_TRANSFER_TYPE} from '@/features/buyers/constants';
import {useTrustTypeStrategy} from '@/features/trust-accounts/providers';

import {OverageNote} from './OverageNote';
import {PaymentMethodCards} from './PaymentMethodCards';

import type {PayinTransferType, PartyType} from '@sollapay/enums';
import type {FC} from 'react';

type TFn = (key: string, opts?: Record<string, unknown>) => string;

/** Parsed `maxAmountNis` when `amount` exceeds it, else `undefined` — shared by the blocking
 *  max-exceeded error and the non-blocking overage warning so the comparison isn't duplicated. */
function exceededMax(amount: string, maxAmountNis: string | undefined): Money | undefined {
  if (!maxAmountNis) return undefined;
  const max = parseMoney(maxAmountNis);
  return parseMoney(amount || '0').gt(max) ? max : undefined;
}

/** Picks between a base key (with percent) and its FullPrice twin, per the strategy's cap hint. */
function capAwareLabel(
  t: TFn,
  prefix: string,
  showsCollectionCapHint: boolean,
  maxPercentage: number,
  baseKey: string,
  fullPriceKey: string,
  opts: Record<string, unknown> = {}
): string {
  return showsCollectionCapHint
    ? t(`${prefix}.${baseKey}`, {...opts, percent: maxPercentage})
    : t(`${prefix}.${fullPriceKey}`, opts);
}

interface AmountValidationParams {
  allowCustom: boolean;
  amountTouched: boolean;
  amount: string;
  maxAmountNis?: string;
  remainingAmountNis?: string;
  maxPercentage: number;
  showsCollectionCapHint: boolean;
  enforcesCollectionCap: boolean;
  t: TFn;
  prefix: string;
}

function getAmountError({
  allowCustom,
  amountTouched,
  amount,
  maxAmountNis,
  remainingAmountNis,
  maxPercentage,
  showsCollectionCapHint,
  enforcesCollectionCap,
  t,
  prefix
}: AmountValidationParams): string | undefined {
  if (!allowCustom || !amountTouched) return undefined;
  if (!amount || amount === '0') return t(`${prefix}.errors.amountRequired`);
  const parsedAmount = parseMoney(amount);
  if (parsedAmount.lt(parseMoney('1'))) return t(`${prefix}.errors.amountAboveZero`);
  // The asset price is a reference figure, not a ceiling, for a trust type that doesn't enforce
  // the collection cap — no amount is ever "too much" here.
  if (!enforcesCollectionCap) return undefined;
  const overMax = exceededMax(amount, maxAmountNis);
  if (overMax) {
    return capAwareLabel(
      t,
      prefix,
      showsCollectionCapHint,
      maxPercentage,
      'errors.amountExceedsMax',
      'errors.amountExceedsMaxFullPrice',
      {max: formatNIS(overMax)}
    );
  }
  if (remainingAmountNis) {
    const remaining = parseMoney(remainingAmountNis);
    if (parsedAmount.toNumber() > remaining.toNumber())
      return t(`${prefix}.errors.amountExceedsRemaining`, {remaining: formatNIS(remaining)});
  }
  return undefined;
}

function getAmountHint(
  allowCustom: boolean,
  maxAmountNis: string | undefined,
  remainingAmountNis: string | undefined,
  maxPercentage: number,
  showsCollectionCapHint: boolean,
  enforcesCollectionCap: boolean,
  t: TFn,
  prefix: string
): string {
  // Not a ceiling here — no "up to X" hint to show; the overage warning banner covers the deal
  // amount when it's actually relevant (i.e. once exceeded).
  if (!enforcesCollectionCap) return '';

  const capLabel = capAwareLabel(
    t,
    prefix,
    showsCollectionCapHint,
    maxPercentage,
    'percentOfPrice',
    'fullPrice'
  );

  if (!allowCustom) return capLabel;
  if (remainingAmountNis && maxAmountNis && remainingAmountNis !== maxAmountNis)
    return t(`${prefix}.amountRemainingHint`, {
      remaining: formatNIS(remainingAmountNis)
    });
  if (maxAmountNis)
    return capAwareLabel(
      t,
      prefix,
      showsCollectionCapHint,
      maxPercentage,
      'amountHint',
      'amountHintFullPrice',
      {max: formatNIS(maxAmountNis)}
    );
  return capLabel;
}

const PERCENT_OPTIONS = [25, 33, 50] as const;

export interface BuyerOption {
  id: string;
  fullName: string;
  partyType: PartyType;
}

export interface PaymentRequestTabContentProps {
  amount: string;
  onAmountChange?: (value: string) => void;
  /** Max collectable = 7% of purchase price */
  maxAmountNis?: string;
  /** Remaining allowed = maxCollectable - already sent (non-terminal) */
  remainingAmountNis?: string;
  /** Asset display label shown as the header (e.g. "דירה 12, קומה 3") */
  assetLabel?: string;
  assetOptions?: {value: string; label: string}[];
  selectedAssetId?: string;
  onAssetChange?: (assetId: string) => void;
  /** All buyers for this purchase — shown in the buyer select */
  buyers: BuyerOption[];
  /** Currently selected buyer ID */
  selectedBuyerId: string;
  onBuyerChange: (buyerId: string) => void;
  initiationMethod: PaymentMethod | '';
  onInitiationMethodChange: (value: PaymentMethod) => void;
  transferType: PayinTransferType;
  onTransferTypeChange: (value: PayinTransferType) => void;
  isZahavOnly: boolean;
  expiryDuration: LinkExpiryDuration;
  onExpiryDurationChange: (value: LinkExpiryDuration) => void;
  saveError?: unknown;
}

export const PaymentRequestTabContent: FC<PaymentRequestTabContentProps> = ({
  amount,
  onAmountChange,
  maxAmountNis,
  remainingAmountNis,
  assetLabel,
  assetOptions,
  selectedAssetId,
  onAssetChange,
  buyers,
  selectedBuyerId,
  onBuyerChange,
  initiationMethod,
  onInitiationMethodChange,
  transferType,
  onTransferTypeChange,
  isZahavOnly,
  expiryDuration,
  onExpiryDurationChange,
  saveError
}) => {
  const {t} = useTranslation('trustAccounts');
  const {
    maxCollectionPercentage: maxPercentage,
    showsCollectionCapHint,
    enforcesCollectionCap
  } = useTrustTypeStrategy();
  const prefix = 'view.buyers.paymentRequest';
  const allowCustom = config.features.allowCustomPaymentAmount;
  const [amountTouched, setAmountTouched] = useState(false);
  const [selectedPercentOption, setSelectedPercentOption] = useState('');

  const partyTypeBadge = (partyType: PartyType) => (
    <Badge variant="neutral" size="sm">
      {t(`${prefix}.partyType.${partyType}`)}
    </Badge>
  );

  const buyerOptions = buyers.map(b => ({
    value: b.id,
    label: b.fullName,
    badge: partyTypeBadge(b.partyType)
  }));
  const selectedBuyer = buyers.find(b => b.id === selectedBuyerId);
  const isPisDisabled = !isPisAllowedForPartyType(selectedBuyer?.partyType);

  const handleMethodChange = (method: PaymentMethod) => {
    if (method !== initiationMethod) onTransferTypeChange(DEFAULT_PAYMENT_TRANSFER_TYPE);
    onInitiationMethodChange(method);
  };

  const linkExpiryOptions = [
    {value: LinkExpiryDuration.OneDay, label: t(`${prefix}.linkExpiryOptions.oneDay`)},
    {value: LinkExpiryDuration.ThreeDays, label: t(`${prefix}.linkExpiryOptions.threeDays`)},
    {value: LinkExpiryDuration.FiveDays, label: t(`${prefix}.linkExpiryOptions.fiveDays`)},
    {value: LinkExpiryDuration.OneWeek, label: t(`${prefix}.linkExpiryOptions.oneWeek`)}
  ];

  const handlePercentageSelect = useCallback(
    (value: string) => {
      if (!onAmountChange || !remainingAmountNis) return;
      setAmountTouched(true);
      setSelectedPercentOption(value);
      onAmountChange(
        value === 'max'
          ? remainingAmountNis
          : Money.from(remainingAmountNis).percentOf(Number(value)).toString()
      );
    },
    [onAmountChange, remainingAmountNis]
  );

  const showPercentageOptions =
    allowCustom &&
    !!onAmountChange &&
    !!remainingAmountNis &&
    parseMoney(remainingAmountNis).toNumber() > 0;

  const percentageOptions = useMemo(
    () => [
      ...PERCENT_OPTIONS.map(pct => ({value: String(pct), label: `${pct}%`})),
      {value: 'max', label: t(`${prefix}.maxButton`)}
    ],
    [t, prefix]
  );

  const amountError = getAmountError({
    allowCustom,
    amountTouched,
    amount,
    maxAmountNis,
    remainingAmountNis,
    maxPercentage,
    showsCollectionCapHint,
    enforcesCollectionCap,
    t,
    prefix
  });

  const amountHint = getAmountHint(
    allowCustom,
    maxAmountNis,
    remainingAmountNis,
    maxPercentage,
    showsCollectionCapHint,
    enforcesCollectionCap,
    t,
    prefix
  );

  const showExpiry = initiationMethod === PaymentMethod.PIS;

  // Non-blocking heads-up for uncapped trust types (e.g. general sale) — never disables submit.
  const overageMax = enforcesCollectionCap ? undefined : exceededMax(amount, maxAmountNis);

  return (
    <FormShell
      id="payment-request-form"
      className="flex flex-col gap-6 py-6"
      onSubmit={e => e.preventDefault()}
      saveError={saveError}
    >
      {assetOptions ? (
        <FieldSelect
          align="start"
          options={assetOptions}
          value={selectedAssetId}
          onValueChange={onAssetChange}
          fieldClassName="*:w-fit"
          triggerClassName="w-fit justify-start gap-2 border-0 bg-transparent p-0 shadow-none transition-opacity hover:opacity-70"
          triggerContent={
            <AssetHeaderLabel
              label={
                assetOptions.find(o => o.value === selectedAssetId)?.label ??
                t(`${prefix}.unitPlaceholder`)
              }
            />
          }
        />
      ) : (
        assetLabel && <AssetHeaderLabel label={assetLabel} />
      )}
      <FieldSelect
        label={t(`${prefix}.buyerName`)}
        placeholder={t(`${prefix}.buyerNamePlaceholder`)}
        options={buyerOptions}
        value={selectedBuyerId}
        onValueChange={onBuyerChange}
        disabled={buyerOptions.length <= 1}
        badge={selectedBuyer && partyTypeBadge(selectedBuyer.partyType)}
        required
      />
      <div className="flex flex-col gap-1">
        <FieldMoneyInput
          label={t(`${prefix}.amount`)}
          value={amount}
          onChange={
            allowCustom && onAmountChange
              ? (val: string) => {
                  setAmountTouched(true);
                  setSelectedPercentOption('');
                  onAmountChange(val);
                }
              : () => {}
          }
          required
          disabled={!allowCustom}
          error={amountError}
        />
        {showPercentageOptions && (
          <ButtonGroup
            variant="pill"
            value={selectedPercentOption}
            onChange={handlePercentageSelect}
            items={percentageOptions}
          />
        )}
        {amountHint && (
          <Typography size="xs" color="tertiary">
            {amountHint}
          </Typography>
        )}
      </div>
      <PaymentMethodCards
        method={initiationMethod}
        onMethodChange={handleMethodChange}
        transferType={transferType}
        onTransferTypeChange={onTransferTypeChange}
        isPisDisabled={isPisDisabled}
        isZahavOnly={isZahavOnly}
      />
      {showExpiry && (
        <div data-slot="link-expiry-field" className="flex flex-col gap-2">
          <FieldSelect
            label={t(`${prefix}.linkExpiry`)}
            labelSuffix={<InfoTooltip content={t(`${prefix}.linkExpiryTooltip`)} />}
            placeholder={t(`${prefix}.linkExpiryPlaceholder`)}
            options={linkExpiryOptions}
            value={expiryDuration}
            onValueChange={val => onExpiryDurationChange(val as LinkExpiryDuration)}
            required
          />
          <div data-slot="reminder-note" className="flex items-center gap-1.5">
            <Icon name="bell-ringing-01" className="size-3.5 shrink-0 text-fg-tertiary" />
            <Typography size="xs" color="tertiary" className="whitespace-normal">
              {t(`${prefix}.reminderNote`)}
            </Typography>
            <InfoTooltip content={t(`${prefix}.reminderTooltip`)} />
          </div>
        </div>
      )}
      {overageMax && (
        <OverageNote
          lead={t(`${prefix}.overageWarningLead`)}
          rest={t(`${prefix}.overageWarningRest`, {max: formatNIS(overageMax)})}
        />
      )}
    </FormShell>
  );
};

function AssetHeaderLabel({label}: {label: string}) {
  return (
    <div className="flex items-center gap-2">
      <IconBox icon="building" size="sm" iconColor="tertiary" />
      <Typography size="lg" weight="semibold">
        {label}
      </Typography>
    </div>
  );
}
